import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, Building2, Mail, Phone,
} from 'lucide-react';
import { empresasService } from '@/services/empresas.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

interface CompanyFormData {
  name: string;
  slug: string;
  rfc: string;
  email: string;
  phone: string;
}

const emptyForm: CompanyFormData = {
  name: '', slug: '', rfc: '', email: '', phone: '',
};

export function CompaniesPage() {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { error, errorType, handleApiError: onApiError, handleAuthRedirect } = useApiError();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any>(null);
  const [form, setForm] = useState<CompanyFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await empresasService.findAll();
      setCompanies(data || []);
    } catch (err: any) { onApiError(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const slugify = (text: string) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const openCreate = async () => {
    setEditingCompany(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = async (c: any) => {
    setEditingCompany(c);
    setForm({
      name: c.name || '',
      slug: c.slug || '',
      rfc: c.rfc || '',
      email: c.email || '',
      phone: c.phone || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.slug.trim()) return;
    setSaving(true);
    try {
      if (editingCompany) {
        await empresasService.update(editingCompany.id, {
          name: form.name,
          slug: form.slug,
          rfc: form.rfc || undefined,
          email: form.email || undefined,
          phone: form.phone || undefined,
        });
      } else {
        await empresasService.create({
          name: form.name,
          slug: form.slug,
          rfc: form.rfc || undefined,
          email: form.email || undefined,
          phone: form.phone || undefined,
        });
      }
      setModalOpen(false);
      load();
    } catch (err: any) { onApiError(err); }
    setSaving(false);
  };

  const handleDeactivate = async (c: any) => {
    if (!await dialogs.confirm(`¿Desactivar la empresa "${c.name}"?\n\nSe desactivarán todos sus usuarios y sucursales.`)) return;
    try {
      await empresasService.deactivate(c.id);
      load();
    } catch (err: any) { onApiError(err); }
  };

  const filtered = companies.filter((c: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.slug?.toLowerCase().includes(q) ||
      c.rfc?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={load} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Empresas</h1>
          <p className="text-sm text-ios-gray mt-1">{companies.length} empresas registradas</p>
        </div>
        <button onClick={openCreate} className="ios-button flex items-center gap-2 px-4 py-2">
          <Plus size={16} />
          Nueva Empresa
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar empresa por nombre, RFC, email..."
          className="ios-input w-full pl-9 h-9 text-sm"
        />
      </div>

      {/* Companies list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Building2 size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">
            {search ? 'Sin resultados' : 'No hay empresas'}
          </h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'Intenta con otros términos' : 'Crea tu primera empresa'}
          </p>
          {!search && (
            <button onClick={openCreate} className="ios-button">
              + Nueva Empresa
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c: any) => (
            <div
              key={c.id}
              className="ios-card p-4 hover:shadow-ios-md transition-all cursor-pointer"
              onClick={() => openEdit(c)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-[14px] flex items-center justify-center flex-shrink-0 ${
                    c.isActive === false ? 'bg-gray-100 text-gray-400' :
                    'bg-gradient-to-br from-ios-blue/10 to-purple-100 text-ios-blue'
                  }`}>
                    <Building2 size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-base font-semibold ${c.isActive === false ? 'text-gray-400' : ''}`}>
                        {c.name}
                      </p>
                      {c.isActive === false && (
                        <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full font-medium">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ios-gray mt-0.5">@{c.slug}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      {c.rfc && (
                        <code className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">
                          {c.rfc}
                        </code>
                      )}
                      {c.email && (
                        <span className="text-[11px] text-ios-gray flex items-center gap-1">
                          <Mail size={11} /> {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="text-[11px] text-ios-gray flex items-center gap-1">
                          <Phone size={11} /> {c.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <div className="text-right">
                    <p className="text-sm font-semibold">{c._count?.branches || 0}</p>
                    <p className="text-[10px] text-ios-gray">Sucursales</p>
                  </div>
                  <div className="w-px h-8 bg-gray-200" />
                  <div className="text-right">
                    <p className="text-sm font-semibold">{c._count?.users || 0}</p>
                    <p className="text-[10px] text-ios-gray">Usuarios</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">
                {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm(p => ({
                      ...p,
                      name,
                      slug: editingCompany ? p.slug : slugify(name),
                    }));
                  }}
                  placeholder="Mi Empresa S.A. de C.V."
                  className="ios-input w-full"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Slug *</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm(p => ({ ...p, slug: slugify(e.target.value) }))}
                  placeholder="mi-empresa"
                  className="ios-input w-full font-mono text-sm"
                  disabled={!!editingCompany}
                />
                <p className="text-[10px] text-ios-gray mt-1">
                  {editingCompany ? 'No se puede cambiar después de crear' : 'Identificador único para la URL'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">RFC</label>
                  <input
                    type="text"
                    value={form.rfc}
                    onChange={(e) => setForm(p => ({ ...p, rfc: e.target.value.toUpperCase() }))}
                    placeholder="XMX010101XXX"
                    className="ios-input w-full font-mono text-sm uppercase"
                  />
                </div>
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Teléfono</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+52..."
                    className="ios-input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="contacto@empresa.com"
                  className="ios-input w-full"
                />
              </div>
            </div>

            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.name.trim() || !form.slug.trim()}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Guardando...' : editingCompany ? 'Actualizar' : 'Crear Empresa'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
